import { Box, Stack } from '@mui/material';
import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type AlwaysVisibleScrollbarStackProps = {
  children: ReactNode;
} & React.ComponentProps<typeof Stack>;

const AlwaysVisibleScrollbarStack: FC<AlwaysVisibleScrollbarStackProps> = ({
  children,
  sx,
  ...stackProps
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const trackPadding = 8;
  const minThumbHeight = 24;

  const [thumbHeight, setThumbHeight] = useState(minThumbHeight);
  const [thumbTop, setThumbTop] = useState(trackPadding);
  const dragStateRef = useRef<{ startY: number; startScrollTop: number } | null>(null);

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollHeight, clientHeight, scrollTop } = el;
    const maxScrollTop = Math.max(1, scrollHeight - clientHeight);

    const trackHeight = Math.max(0, clientHeight - 2 * trackPadding);
    const rawThumbHeight = (clientHeight / Math.max(scrollHeight, 1)) * trackHeight;
    const nextThumbHeight = Math.max(minThumbHeight, Math.min(trackHeight, rawThumbHeight));

    const maxThumbTop = Math.max(0, trackHeight - nextThumbHeight);
    const nextThumbTop = (scrollTop / maxScrollTop) * maxThumbTop;

    setThumbHeight(nextThumbHeight);
    setThumbTop(trackPadding + (Number.isFinite(nextThumbTop) ? nextThumbTop : 0));
  }, [minThumbHeight, trackPadding]);

  useLayoutEffect(() => {
    updateThumb();
    const el = scrollRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => updateThumb());
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [updateThumb]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const el = scrollRef.current;
      const dragState = dragStateRef.current;
      if (!el || !dragState) return;

      const { scrollHeight, clientHeight } = el;
      const maxScrollTop = Math.max(1, scrollHeight - clientHeight);

      const trackHeight = Math.max(0, clientHeight - 2 * trackPadding);
      const maxThumbTop = Math.max(1, trackHeight - thumbHeight);
      const deltaY = e.clientY - dragState.startY;
      const scrollDelta = (deltaY / maxThumbTop) * maxScrollTop;

      el.scrollTop = dragState.startScrollTop + scrollDelta;
      updateThumb();
    };

    const onMouseUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [thumbHeight, trackPadding, updateThumb]);

  const stopParentScrollIfNeeded = useCallback((deltaY: number) => {
    const el = scrollRef.current;
    if (!el) return false;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    const scrollingDown = deltaY > 0;
    const scrollingUp = deltaY < 0;

    const canScrollDown = !atBottom;
    const canScrollUp = !atTop;

    return (scrollingDown && canScrollDown) || (scrollingUp && canScrollUp);
  }, []);

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Stack
        ref={scrollRef}
        {...stackProps}
        sx={{
          overflowY: 'auto',
          height: '100%',
          pr: '1.25rem',
          scrollbarWidth: 'none',
          overscrollBehavior: 'contain',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          ...(sx as any),
        }}
        onScroll={updateThumb}
        onWheel={(e) => {
          if (stopParentScrollIfNeeded(e.deltaY)) {
            e.stopPropagation();
          }
        }}
        onTouchMove={(e) => {
          e.stopPropagation();
        }}
      >
        {children}
      </Stack>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 6,
          width: 10,
          borderRadius: '999px',
          backgroundColor: 'rgba(22, 41, 54, 0.06)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: `${thumbTop}px`,
          right: 7,
          width: 8,
          height: `${thumbHeight}px`,
          borderRadius: '999px',
          backgroundColor: '#B6B6BA',
          cursor: 'grab',
          pointerEvents: 'auto',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
        onMouseDown={(e) => {
          const el = scrollRef.current;
          if (!el) return;
          dragStateRef.current = { startY: e.clientY, startScrollTop: el.scrollTop };
        }}
      />
    </Box>
  );
};

export default AlwaysVisibleScrollbarStack;
